import React, { useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import SortIcon from "@mui/icons-material/Sort";
import { SORT_FIELDS } from "../../utils/taskSort";

/**
 * Right-aligned control cluster shared by every TaskManagement page: a sort
 * menu (applies to all views), a column chooser (Table view only), and the
 * List / Table / Kanban toggle.
 *
 * @param {Object} props
 * @param {string} props.viewMode
 * @param {function} props.onViewModeChange
 * @param {{field:string,dir:('asc'|'desc')}[]} props.sortCriteria
 * @param {function} props.onSortChange
 * @param {Object} [props.visibleCols] - Column visibility map (Table view)
 * @param {function} [props.onToggleColumn] - Toggles a single column
 * @returns {React.ReactElement}
 */
const VIEW_BUTTONS = [
  { value: "list", label: "list view", Icon: ListIcon },
  { value: "table", label: "table view", Icon: TableRowsIcon },
  { value: "kanban", label: "kanban view", Icon: ViewKanbanIcon },
];

const TaskViewControls = ({
  viewMode,
  onViewModeChange,
  sortCriteria = [],
  onSortChange,
  visibleCols,
  onToggleColumn,
  availableViews = ["list", "table", "kanban"],
}) => {
  const [anchorSort, setAnchorSort] = useState(null);
  const [anchorCols, setAnchorCols] = useState(null);

  const primary = sortCriteria[0] || null;
  const activeField = primary?.field || "";
  const dir = primary?.dir || "asc";

  const setField = (field) => {
    onSortChange(field ? [{ field, dir }] : []);
    setAnchorSort(null);
  };
  const setDir = (newDir) => {
    if (newDir && activeField) onSortChange([{ field: activeField, dir: newDir }]);
  };

  const showColumns = viewMode === "table" && visibleCols && onToggleColumn;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Tooltip title="Sort">
        <IconButton
          size="small"
          onClick={(e) => setAnchorSort(e.currentTarget)}
          color={activeField ? "primary" : "default"}
        >
          <SortIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {showColumns && (
        <Tooltip title="Choose columns">
          <IconButton size="small" onClick={(e) => setAnchorCols(e.currentTarget)}>
            <ViewColumnIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {availableViews.length > 1 && (
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, v) => v && onViewModeChange(v)}
          size="small"
          sx={{ ml: 0.5 }}
        >
          {VIEW_BUTTONS.filter((b) => availableViews.includes(b.value)).map(
            ({ value, label, Icon }) => (
              <ToggleButton key={value} value={value} aria-label={label}>
                <Icon />
              </ToggleButton>
            ),
          )}
        </ToggleButtonGroup>
      )}

      <Menu
        anchorEl={anchorSort}
        open={Boolean(anchorSort)}
        onClose={() => setAnchorSort(null)}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 220 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Sort by
          </Typography>
          <ToggleButtonGroup
            value={dir}
            exclusive
            onChange={(e, v) => setDir(v)}
            size="small"
            fullWidth
            disabled={!activeField}
          >
            <ToggleButton value="asc">Asc</ToggleButton>
            <ToggleButton value="desc">Desc</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Divider />
        <MenuItem selected={!activeField} onClick={() => setField("")}>
          <ListItemText primary="None" />
        </MenuItem>
        {SORT_FIELDS.map((f) => (
          <MenuItem
            key={f.key}
            selected={activeField === f.key}
            onClick={() => setField(f.key)}
          >
            <ListItemText primary={f.label} />
          </MenuItem>
        ))}
      </Menu>

      {showColumns && (
        <Menu
          anchorEl={anchorCols}
          open={Boolean(anchorCols)}
          onClose={() => setAnchorCols(null)}
        >
          {Object.keys(visibleCols).map((k) => (
            <MenuItem key={k} onClick={() => onToggleColumn(k)}>
              <Checkbox size="small" checked={visibleCols[k]} />
              <ListItemText primary={k === "task_class" ? "class" : k.replace(/_/g, " ")} />
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
};

export default TaskViewControls;
